import React from 'react';
import { CredentialDetailsProps } from '../../types/VerificationTypes';
import { Badge } from '../ui/badge';

const CredentialDetails: React.FC<CredentialDetailsProps> = ({ credential }) => {
  const formatFieldValue = (value: any): string => {
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }
    if (value instanceof Date) {
      return value.toLocaleDateString();
    }
    return String(value);
  };

  const getFieldType = (value: any): 'string' | 'date' | 'number' | 'boolean' => {
    if (typeof value === 'boolean') return 'boolean';
    if (typeof value === 'number') return 'number';
    if (value instanceof Date || (typeof value === 'string' && !isNaN(Date.parse(value)))) {
      return 'date';
    }
    return 'string';
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 md:p-6 mb-6">
      <h3 className="text-lg md:text-xl font-semibold text-charcoal-ink mb-4 md:mb-6">Credential Details</h3>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
        {/* Left Column - Credential Information */}
        <div>
          <h4 className="text-base md:text-lg font-medium text-charcoal-ink mb-3 md:mb-4">Credential Information</h4>
          <div className="space-y-3 md:space-y-4">
            <div>
              <p className="text-xs md:text-sm text-warm-slate mb-1">Schema Name</p>
              <p className="font-medium text-charcoal-ink text-sm md:text-base">{credential.schema_name}</p>
            </div>
            
            <div>
              <p className="text-xs md:text-sm text-warm-slate mb-1">Holder</p>
              <p className="font-medium text-charcoal-ink text-sm md:text-base">{credential.holder_name}</p>
            </div>
            
            <div>
              <p className="text-xs md:text-sm text-warm-slate mb-1">Issuer</p>
              <p className="font-medium text-charcoal-ink text-sm md:text-base">{credential.issuer_name}</p>
            </div>
            
            <div>
              <p className="text-xs md:text-sm text-warm-slate mb-1">Status</p>
              <Badge 
                className={
                  credential.status === 'active' 
                    ? 'bg-verified-green text-white text-xs md:text-sm' 
                    : 'bg-red-500 text-white text-xs md:text-sm'
                }
              >
                {credential.status}
              </Badge>
            </div>
            
            <div>
              <p className="text-xs md:text-sm text-warm-slate mb-1">Issued Date</p>
              <p className="font-medium text-charcoal-ink text-sm md:text-base">
                {new Date(credential.issued_at).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </p>
            </div>
          </div>
        </div>
        
        {/* Right Column - Credential Fields */}
        <div>
          <h4 className="text-base md:text-lg font-medium text-charcoal-ink mb-3 md:mb-4">Credential Fields</h4>
          <div className="space-y-3 md:space-y-4">
            {Object.entries(credential.fields).map(([key, value]) => (
              <div key={key}>
                <p className="text-xs md:text-sm text-warm-slate mb-1 capitalize">
                  {key.replace(/_/g, ' ')}
                </p>
                <p className="font-medium text-charcoal-ink text-sm md:text-base">
                  {formatFieldValue(value)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CredentialDetails; 